/**
 * Trend Audio Player — frontend controller.
 *
 * Reads data-audio-url and data-post-id from the block wrapper.
 * Uses SoundTouchJS AudioWorklet for high-quality pitch-preserving
 * playback speed control (WSOLA algorithm). Falls back to native
 * audio.playbackRate on browsers without AudioWorklet support.
 */
import { SoundTouchNode } from '@soundtouchjs/audio-worklet';

'use strict';

const RING_C = 75.4; // 2π × 12 (ring radius in SVG)

function formatTime( seconds ) {
	if ( isNaN( seconds ) || ! isFinite( seconds ) ) return '00:00';
	const m = Math.floor( seconds / 60 );
	const s = Math.floor( seconds % 60 );
	return String( m ).padStart( 2, '0' ) + ':' + String( s ).padStart( 2, '0' );
}

function initPlayer( wrapper ) {
	const audio     = wrapper.querySelector( '.tap-audio-el' );
	const playBtn   = wrapper.querySelector( '.tap-play-btn' );
	const label     = wrapper.querySelector( '.tap-label' );
	const loadingEl = wrapper.querySelector( '.tap-loading' );
	const errorEl   = wrapper.querySelector( '.tap-error' );
	const errorMsg  = wrapper.querySelector( '.tap-error-msg' );
	const retryBtn  = wrapper.querySelector( '.tap-retry-btn' );
	const timeTotal = wrapper.querySelector( '.tap-time-total' );
	const ringFill  = wrapper.querySelector( '.tap-ring-fill' );

	const speedWrap = wrapper.querySelector( '.tap-speed-wrap' );
	const speedBtn  = wrapper.querySelector( '.tap-speed-btn' );
	const speedLbl  = wrapper.querySelector( '.tap-speed-label' );
	const speedMenu = wrapper.querySelector( '.tap-speed-menu' );
	const speedOpts = wrapper.querySelectorAll( '.tap-speed-opt' );

	const postId      = wrapper.dataset.postId;
	const restUrl     = wrapper.dataset.restUrl;
	const nonce       = wrapper.dataset.nonce;
	const isAdmin     = wrapper.dataset.admin === '1';
	const processorUrl = wrapper.dataset.processorUrl;
	const loadingMsg  = wrapper.querySelector( '.tap-loading-msg' );
	let   audioUrl    = wrapper.dataset.audioUrl;
	let   loaded      = false;
	let   audioCtx    = null;
	let   stNode      = null;

	// Allow createMediaElementSource (required even for same-origin in some configurations)
	audio.crossOrigin = 'anonymous';

	const SPEED_KEY  = 'tap_playback_speed';
	let currentSpeed = parseFloat( localStorage.getItem( SPEED_KEY ) || '1' );

	function applySpeed( speed ) {
		currentSpeed = speed;
		audio.playbackRate = speed;
		if ( stNode ) {
			// SoundTouch handles pitch correction; disable the browser's own algorithm.
			audio.preservesPitch = false;
			stNode.playbackRate.value = speed;
		} else {
			// No SoundTouch yet (or unavailable) — keep browser pitch correction on.
			audio.preservesPitch = true;
		}
		const display = Number.isInteger( speed ) ? speed + '×' : speed + '×';
		speedLbl.textContent = display;
		speedBtn.setAttribute( 'aria-label', 'Playback speed: ' + display );
		speedBtn.classList.toggle( 'is-active', speed !== 1 );
		speedOpts.forEach( opt => {
			const match = parseFloat( opt.dataset.speed ) === speed;
			opt.classList.toggle( 'tap-speed-opt--active', match );
			opt.setAttribute( 'aria-selected', match ? 'true' : 'false' );
		} );
		localStorage.setItem( SPEED_KEY, speed );
	}

	applySpeed( currentSpeed );

	function openSpeedMenu() {
		speedMenu.hidden = false;
		speedBtn.setAttribute( 'aria-expanded', 'true' );
	}

	function closeSpeedMenu() {
		speedMenu.hidden = true;
		speedBtn.setAttribute( 'aria-expanded', 'false' );
	}

	speedBtn.addEventListener( 'click', ( e ) => {
		e.stopPropagation();
		speedMenu.hidden ? openSpeedMenu() : closeSpeedMenu();
	} );

	speedOpts.forEach( opt => {
		opt.addEventListener( 'click', () => {
			applySpeed( parseFloat( opt.dataset.speed ) );
			closeSpeedMenu();
		} );
	} );

	document.addEventListener( 'click', () => closeSpeedMenu() );
	speedMenu.addEventListener( 'click', e => e.stopPropagation() );

	function fadeLabelTo( text ) {
		if ( label.textContent === text ) return;
		label.classList.add( 'is-fading' );
		setTimeout( () => {
			label.textContent = text;
			label.classList.remove( 'is-fading' );
		}, 110 );
	}

	function showError( msg ) {
		loadingEl.style.display = 'none';
		if ( ! isAdmin ) {
			wrapper.style.display = 'none';
			return;
		}
		errorMsg.textContent  = msg || 'Something went wrong. Please try again.';
		errorEl.style.display = '';
		playBtn.removeAttribute( 'aria-disabled' );
		setPaused();
	}

	function showLoading() {
		errorEl.style.display   = 'none';
		loadingEl.style.display = '';
		playBtn.setAttribute( 'aria-disabled', 'true' );
	}

	function showPlayer() {
		loadingEl.style.display = 'none';
		errorEl.style.display   = 'none';
		playBtn.removeAttribute( 'aria-disabled' );
	}

	function setPlaying() {
		playBtn.classList.add( 'is-playing' );
		fadeLabelTo( 'Pause' );
		playBtn.setAttribute( 'aria-label', 'Pause audio' );
		speedWrap.hidden = false;
	}

	function setPaused() {
		playBtn.classList.remove( 'is-playing' );
		fadeLabelTo( 'Listen' );
		playBtn.setAttribute( 'aria-label', 'Listen to this article' );
	}

	function updateProgress() {
		if ( ! audio.duration ) return;
		const pct = audio.currentTime / audio.duration;
		ringFill.style.strokeDashoffset = RING_C * ( 1 - pct );
		timeTotal.textContent = formatTime( audio.duration - audio.currentTime );
	}

	async function ensureAudio() {
		if ( audioUrl ) return true;

		if ( ! isAdmin ) {
			wrapper.style.display = 'none';
			return false;
		}

		if ( loadingMsg ) loadingMsg.textContent = 'Generating audio…';
		showLoading();
		try {
			const res = await fetch( restUrl + '/generate', {
				method:  'POST',
				headers: {
					'Content-Type': 'application/json',
					'X-WP-Nonce':   nonce,
				},
				body: JSON.stringify( { post_id: parseInt( postId, 10 ) } ),
			} );
			const data = await res.json();
			if ( ! res.ok ) {
				showError( data.message || 'Audio generation failed.' );
				return false;
			}
			audioUrl = data.url;
			wrapper.dataset.audioUrl = audioUrl;
			return true;
		} catch ( err ) {
			showError( 'Network error. Please try again.' );
			return false;
		}
	}

	async function setupWebAudio() {
		if ( audioCtx || ! window.AudioContext || ! window.AudioWorklet || ! processorUrl ) {
			return;
		}
		try {
			audioCtx = new AudioContext();
			await SoundTouchNode.register( audioCtx, processorUrl );
			const source = audioCtx.createMediaElementSource( audio );
			stNode = new SoundTouchNode( { context: audioCtx } );
			stNode.connect( audioCtx.destination );
			source.connect( stNode );
			// Disable browser pitch correction — SoundTouch is now the pitch processor.
			audio.preservesPitch = false;
			stNode.playbackRate.value = currentSpeed;
		} catch ( e ) {
			// Fall back to native playbackRate — quality is acceptable on Safari/Firefox.
			audioCtx = null;
			stNode   = null;
			audio.preservesPitch = true;
		}
	}

	async function loadAndPlay() {
		const ok = await ensureAudio();
		if ( ! ok ) return;

		if ( ! loaded ) {
			showLoading();
			audio.src = audioUrl;
			audio.load();
			loaded = true;
		}

		await setupWebAudio();

		if ( audioCtx && audioCtx.state === 'suspended' ) {
			await audioCtx.resume();
		}

		audio.play().then( () => {
			showPlayer();
			setPlaying();
		} ).catch( ( err ) => {
			showError( err.message || 'Playback failed.' );
		} );
	}

	playBtn.addEventListener( 'click', () => {
		if ( playBtn.getAttribute( 'aria-disabled' ) === 'true' ) return;
		if ( audio.paused ) {
			loadAndPlay();
		} else {
			audio.pause();
		}
	} );

	audio.addEventListener( 'play',  () => { showPlayer(); setPlaying(); } );
	audio.addEventListener( 'pause', () => setPaused() );
	audio.addEventListener( 'ended', () => {
		setPaused();
		audio.currentTime = 0;
		ringFill.style.strokeDashoffset = RING_C;
		timeTotal.textContent = formatTime( audio.duration );
	} );
	audio.addEventListener( 'timeupdate', updateProgress );
	audio.addEventListener( 'durationchange', () => {
		timeTotal.textContent = formatTime( audio.duration - audio.currentTime );
	} );
	audio.addEventListener( 'waiting', () => {
		loadingEl.style.display = '';
	} );
	audio.addEventListener( 'canplay', () => {
		loadingEl.style.display = 'none';
	} );
	audio.addEventListener( 'error', () => {
		showError( 'Could not load audio. The file may be missing.' );
	} );

	retryBtn.addEventListener( 'click', () => {
		errorEl.style.display = 'none';
		loaded = false;
		loadAndPlay();
	} );

	requestAnimationFrame( () => requestAnimationFrame( () => {
		wrapper.classList.add( 'tap-js-ready' );
	} ) );
}

function init() {
	document.querySelectorAll( '.trend-audio-player' ).forEach( initPlayer );
}

if ( document.readyState === 'loading' ) {
	document.addEventListener( 'DOMContentLoaded', init );
} else {
	init();
}
